/**
 * Admin panel — polskie tłumaczenia.
 *
 * Source-of-truth dla `AdminMessagesShape` — TypeScript wymusi że `en.ts`
 * dostarcza dokładnie te same klucze.
 *
 * Stringi zorganizowane semantycznie per komponent (`topBar`, `layoutPanel`,
 * `alignmentChecker`, ...). Sparametryzowane stringi to funkcje:
 *
 *     t.pendingBar.statusLabel(3) // -> "Pracuję… (3)"
 *
 * Plurals: funkcje używają `Intl.PluralRules` (PL: one/few/many/other) gdy
 * gramatyka wymaga, ALBO ręcznych warunków gdy fraza jest prostsza.
 */

const plPlural = new Intl.PluralRules("pl");

export const adminMessagesPl = {
  topBar: {
    adminPanelLink: {
      title: "Otwórz panel administracyjny Imperial w nowej karcie",
      label: "Panel administracyjny Imperial",
    },
    devBannerToggle: {
      titleShown: "Ukryj banner developera (AlignmentChecker)",
      titleHidden: "Pokaż banner developera (AlignmentChecker)",
      ariaShown: "Ukryj banner developera",
      ariaHidden: "Pokaż banner developera",
    },
    devMockDropDownTreeToggle: {
      titleOn: "Wyłącz mock-drzewo (5 poziomów) w WDropDownMenu",
      titleOff: "Włącz mock-drzewo (5 poziomów) w WDropDownMenu",
      ariaOn: "Wyłącz przykładowe drzewo 5-poziomowe",
      ariaOff: "Włącz przykładowe drzewo 5-poziomowe",
    },
    editModeToggle: {
      titleOpen: "Zarządzanie układem otwarte — kliknij żeby zamknąć",
      titleClosed: "Otwórz panel zarządzania układem strony i oknami",
      label: "Zarządzaj układem strony i oknami",
    },
    notificationsBell: {
      ariaLabel: (count: number) => `Powiadomienia (${count} nieprzeczytanych)`,
      emptyState: "Brak powiadomień",
    },
    tabCatalog: {
      titleOpen: "Zarządzanie katalogami otwarte — kliknij żeby zamknąć",
      titleClosed: "Otwórz panel zarządzania katalogami",
      label: "Zarządzaj katalogami",
    },
    themeToggle: {
      title: (label: string) => `Motyw: ${label} (kliknij żeby zmienić)`,
      ariaLabel: (label: string) => `Zmień motyw, obecny: ${label}`,
      modes: {
        light: "jasny",
        dark: "ciemny",
        funeral: "żałobny",
        hc: "kontrastowy",
      },
    },
    userMenu: {
      yourAccount: "Twoje konto",
      logout: "Wyloguj",
      loggingOut: "Wylogowywanie...",
      toastLoggedOut: "Wylogowano z Imperial.",
      toastError: (reason: string) => `Błąd wylogowania: ${reason}`,
      toastErrorUnknown: "nieznany",
    },
    adminLocaleSwitch: {
      triggerTitle: (label: string) =>
        `Zarządzasz wersją: ${label}. Kliknij aby przełączyć.`,
      menuAriaLabel: "Zarządzane języki",
      menuHeader: "Zarządzaj wersją językową",
      menuFooter: "Każda wersja językowa ma własny układ + okna.",
    },
  },

  pendingBar: {
    ariaLabel: (count: number) =>
      `Pracuję — ${count} ${pluralRequestsPl(count)} w toku`,
    statusLabel: (count: number) => `Pracuję… (${count})`,
  },

  catalogToolbar: {
    ariaLabel: "Zarządzanie katalogiem",
    closeAria: "Zamknij",
    closeTitle: "Zamknij",
    addRootTitle:
      "Otwórz Imperial Administrator > Katalogi > formularz dodawania (nowa karta)",
    addRootLabel: "Dodaj nowy katalog",
    addRootHint: "Otwiera Imperial Administrator w nowej karcie",
    manageExistingHeading: "Chcesz zarządzać istniejącym katalogiem?",
    manageExistingBody:
      'Z poziomu strony głównej możesz tylko dodać nowy katalog. Żeby skonfigurować, edytować treść lub usunąć istniejący katalog, przejdź do niego (np. kliknij link w menu, lub wpisz adres ręcznie w przeglądarce). Tab "Zarządzaj katalogami" otwarty na podstronie katalogu pokaże akcje dla tego katalogu.',
    addChildTitle:
      "Otwórz Imperial Administrator > Katalogi > Dodaj — z preset'em id_parent (nowa karta)",
    addChildLabel: "Dodaj podstronę",
    addChildHint: "Jako dziecko tego katalogu",
    configureTitle:
      "Otwórz Imperial Administrator > Katalogi > Edytuj — nazwa, symbol, layout, metatags (nowa karta)",
    configureLabel: "Konfiguruj",
    configureHint: "Nazwa, symbol, layout, metatags",
    manageContentTitle:
      "Otwórz Imperial Administrator > Edycja zawartości katalogu (nowa karta)",
    manageContentLabel: "Zarządzaj treścią",
    manageContentHint: "Edycja artykułów / produktów / wpisów",
    deleteConfirmTitle: "Usunąć katalog?",
    deleteConfirmMessage: (catalogSymbol: string, idCatalog: number) =>
      `Czy na pewno chcesz usunąć katalog "${catalogSymbol}" (ID ${idCatalog})? Operacja jest nieodwracalna — katalog zniknie wraz z całą zawartością (artykułami, produktami itp.) oraz przypisaniami okien i layoutów.`,
    deleteConfirmLabel: "Usuń katalog",
    cancelLabel: "Anuluj",
    currentCatalogLabel: "Aktualny katalog:",
    currentCatalogMeta: (id: number | null, slug: string) =>
      `ID ${id ?? "?"} · ścieżka ${slug}`,
    deleteCardLabel: "Usuń",
    deleteCardHint: "Z potwierdzeniem",
    deleteCardTitle: "Usuń katalog — wymaga potwierdzenia, operacja nieodwracalna",
    missingIdCatalogNote:
      "Brak id_catalog w context'cie — strona nie została rozpoznana jako katalog. Sprawdź backend resolve [...slug].",
    /* Komunikaty disabled state — gdy admin nie ma per-katalog uprawnień
       z `imp_user_group_privilege`. Backend `IUSER_PRIVILEGES::hasCatalogPrivilege`
       jest SoT — frontend gate'uje przyciski symetrycznie (modify / delete). */
    noPermissionAddChildTitle:
      "Brak uprawnień: nie możesz dodawać podkatalogów w tym katalogu. Skontaktuj się z administratorem aby uzyskać dostęp \"modify\" dla tego katalogu.",
    noPermissionConfigureTitle:
      "Brak uprawnień: nie możesz edytować właściwości tego katalogu (nazwa, symbol, layout, metatags). Wymagane: \"modify\" dla tego katalogu w grupie użytkownika.",
    noPermissionManageContentTitle:
      "Brak uprawnień: nie możesz edytować zawartości tego katalogu. Wymagane: \"modify\" dla tego katalogu w grupie użytkownika.",
    noPermissionDeleteTitle:
      "Brak uprawnień: nie możesz usunąć tego katalogu. Wymagane: \"delete\" dla tego katalogu oraz pełne prawa katalogowe lub status właściciela (redaktorom usuwanie zablokowane).",
    permissionBadgeLabel: "Twoje uprawnienia:",
    permissionBadgeFull: "Pełen dostęp",
    permissionBadgeOwner: "Właściciel katalogu",
    permissionBadgeRedactor: "Redaktor (ograniczone)",
    permissionBadgeRestricted: "Ograniczone uprawnienia",
    /* Drukuj do PDF (zastępuje legacy IPRINT) - widoczne tylko gdy rozszerzenie
       generatora PDF + silnik Chromium dostępne (`pdf_export_available`). */
    printPdfLabel: "Drukuj do PDF",
    printPdfLabelBusy: "Generowanie...",
    printPdfHint: "Pobierz całą stronę",
    printPdfTitle: "Pobierz całą bieżącą stronę jako plik PDF",
    noPermissionPrintPdfTitle:
      "Brak uprawnień: nie możesz wygenerować wydruku tej strony. Wymagane: zarządzanie treścią (\"manage_content\").",
    printPdfSuccess: "Wygenerowano plik PDF - pobieranie rozpoczęte.",
    printPdfError: "Nie udało się wygenerować pliku PDF. Spróbuj ponownie.",
  },

  layoutPanel: {
    columnHeading: "Zarządzanie układem strony",
    noLayoutsForTemplatePlaceholder: (template: string) =>
      `Szablon "${template}" — brak przypisanych layoutów`,
    layoutChangeReloadHint: "Po zmianie strona przeładuje się z nową strukturą obszarów.",
    inheritsDefaultBannerPrefix: "Bieżąca podstrona dziedziczy",
    inheritsDefaultBannerBold: "układ domyślny",
    inheritsDefaultBannerSuffix:
      ". Każda zmiana którą zatwierdzisz utworzy własną konfigurację tej podstrony (układ domyślny pozostanie bez zmian — aby go zaktualizować, użyj przycisku poniżej).",
    modifiedFromDefaultBannerPrefix: "Bieżąca podstrona ma",
    modifiedFromDefaultBannerBold: "własną konfigurację różną od układu domyślnego",
    modifiedFromDefaultBannerSuffix:
      ". Aby wrócić do układu domyślnego użyj przycisku \"Przywróć ustawienia domyślne\" poniżej.",
    noDefaultBannerBold: "Brak zapisanego domyślnego układu",
    noDefaultBannerSuffix:
      " dla podstron. Skonfiguruj layout + okna jak chcesz, a potem kliknij przycisk poniżej żeby nowe podstrony zaczęły dziedziczyć tę konfigurację.",
    saveDefaultDisabledNoLayouts:
      "Najpierw przypisz layouty do aktywnego szablonu w Imperial Administrator",
    saveDefaultTooltipOverwrite: "Nadpisz istniejący domyślny układ bieżącą konfiguracją",
    saveDefaultTooltipFresh: "Zapisz bieżący układ + okna jako domyślne dla podstron",
    overwriteDialogTitle: "Nadpisać istniejący domyślny układ?",
    saveAsDefaultDialogTitle: "Zapisać jako domyślny układ?",
    overwriteDialogMessageFull:
      "Istniejący domyślny układ ZOSTANIE NADPISANY bieżącą konfiguracją. Wszystkie podstrony używające domyślnego od razu zaczną wyświetlać nowy układ. Tej operacji nie da się cofnąć — kontynuować?",
    saveDefaultDialogMessageFull:
      "Bieżący układ + okna zostaną zapisane jako domyślne dla wszystkich katalogów które nie mają własnej konfiguracji.",
    changeLayoutDialogTitle: "Zmiana układu strony",
    changeLayoutDialogMessage: (
      totalWindows: number,
      affectedCount: number,
      areaNames: string,
    ) => {
      const windowsWord = pluralWindowGenitivePl(totalWindows);
      const areasWord = affectedCount === 1 ? "obszaru" : "obszarów";
      return `Zmiana spowoduje usunięcie ${totalWindows} ${windowsWord} z ${areasWord}: ${areaNames}.\n\nOkna wrócą do "Biblioteki okien" w panelu po prawej — będziesz mógł je przeciągnąć do innego obszaru.`;
    },
    changeLayoutConfirmLabel: "Zmień układ",
    cancelLabel: "Anuluj",
    layoutChangedReturnedToast: (totalWindows: number) => {
      const phrase = pluralWindowsReturnedPl(totalWindows);
      return `Układ zmieniony — ${totalWindows} ${phrase} do listy nieopublikowanych`;
    },
    layoutChangedToast: (layoutName: string) =>
      `Układ zmieniony na "${layoutName}"`,
    defaultLayoutOnlySubpageError:
      "Domyślny układ można zapisać tylko z poziomu podstrony katalogu.",
    pickLayoutFirstError: "Wybierz najpierw layout w polu wyżej.",
    overwriteDefaultMessageSubpage:
      "Istniejący domyślny układ ZOSTANIE NADPISANY bieżącą konfiguracją. Wszystkie podstrony używające domyślnego od razu zaczną wyświetlać nowy układ.",
    overwriteDefaultMessageHomepage: "Istniejący domyślny układ ZOSTANIE NADPISANY bieżącą konfiguracją.",
    overwriteConfirmLabel: "Tak, nadpisz",
    saveAsDefaultConfirmLabel: "Zapisz jako domyślny",
    saveDefaultErrorToast: (reason: string) =>
      `Nie udało się zapisać domyślnego układu: ${reason}.`,
    overwriteDefaultSuccessToast: (windowsInserted: number) =>
      `Domyślny układ został nadpisany (${windowsInserted} okien).`,
    saveDefaultSuccessToast: (windowsInserted: number) =>
      `Domyślny układ został zapisany (${windowsInserted} okien).`,
    panelAriaLabel: "Zarządzanie oknami",
    closeAria: "Zamknij",
    closePanelTitle: "Zamknij panel",
    subpageLayoutHeading: "Layout podstrony",
    homepageLayoutHeading: "Layout strony głównej",
    noTemplateSetPlaceholder: "Brak ustawionego szablonu domeny",
    helpTextSubpage: "Layout dla tej podstrony. Zmiana wpływa tylko na ten URL.",
    helpTextHomepage:
      "Layout strony głównej. Każda podstrona ma swój niezależny.",
    helpTextSuffix: "Po zmianie strona",
    overrideHint:
      'Zmiana utworzy osobną konfigurację tej podstrony (zamiast dziedziczyć z szablonu — użyj przycisku "Zapisz domyślny" poniżej).',
    noDefaultWarning:
      'Brak domyślnego — nowe podstrony będą musiały mieć layout wybrany ręcznie. "Zapisz domyślny" żeby nowe podstrony zaczęły dziedziczyć bieżącą konfigurację.',
    overrideDefaultLabel: "Nadpisz domyślny układ",
    saveAsDefaultLabel: "Zapisz jako domyślny",
    addWindowTitle: "Dodaj nowe okno w Imperial Administrator (otworzy nową kartę)",
    allTypesOption: "Wszystkie typy",
    deleteWindowConfirmLabel: "Usuń okno",
    deleteWindowSuccessToast: (name: string) => `Okno "${name}" zostało usunięte.`,
    deleteWindowErrorToast: (name: string, reason: string) =>
      `Nie udało się usunąć okna "${name}": ${reason}`,
    notesAriaLabel: "Notatki administratora dla okna (najedź by zobaczyć)",
    noNotesAriaLabel: "Brak notatek administratora",
    noNotesTitle:
      "Brak notatek administratora dla tego okna. Uzupełnij pole opisu w Imperial Administrator > Windows Manager > Edytuj okno.",
    openConfigTitle:
      "Otwórz stronę konfiguracji okna w Imperial Administrator (nowa karta)",
    openContentEditorTitle:
      "Otwórz edytor treści okna w Imperial Administrator (nowa karta)",
    contentEditorBlockedDefault: "Komponent nie udostępnia zarządzania treścią.",
    deleteWindowTitle: "Usuń to okno całkowicie z bazy danych",
    emptyLibraryMessage:
      'Wszystkie dostępne okna są już na tej stronie. Aby utworzyć nowe, kliknij "Dodaj".',
    miniMapAriaLabel: "Mini-mapa układu strony",
    discardChangesTitle: "Odrzuć wszystkie niezapisane zmiany",
    commitChangesTitle: "Zapisz zmiany i pokaż na stronie",
    areaUnavailableAria: (label: string) =>
      `${label} — obszar niedostępny dla okien`,
    detachFromAreaTitle:
      "Zdejmij z obszaru — okno wróci do listy nieopublikowanych",
    detachFromAreaShort: "Zdejmij z obszaru (wróci do listy nieopublikowanych)",
    addButtonLabel: "Dodaj",
    sectionCollapseTitle: "Zwiń sekcję",
    sectionExpandTitle: "Rozwiń sekcję",
    libraryDropHint:
      "Przeciągnij okno na układ strony poniżej, aby dodać je do tej strony. Okno może być użyte na wielu stronach jednocześnie.",
    componentFilterTitle: "Filtruj po typie komponentu",
    componentFilterAriaLabel: "Typ komponentu",
    searchPlaceholder: "Szukaj okna…",
    deleteWindowDialogTitle: "Usunąć okno?",
    deleteWindowDialogMessage: (name: string) =>
      `Czy na pewno chcesz usunąć okno "${name}" CAŁKOWICIE? Operacja jest nieodwracalna. Okno zostanie usunięte z bazy danych wraz z konfiguracją i ewentualną treścią.`,
    tileExpandedTooltip: "Kliknij ponownie, aby zwinąć szczegóły",
    tileCompatibleTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — kliknij dla szczegółów, przeciągnij aby opublikować`,
    tileIncompatibleTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — kliknij dla szczegółów (niedostępny dla obecnego układu)`,
    tileCollapseHint: "kliknij, aby zwinąć",
    actionWindowConfig: "Konfiguracja okna",
    actionManageContent: "Zarządzaj treścią okna",
    actionDeleteWindow: "Usuń okno",
    noWindowsMatchingFilters:
      "Brak okien spełniających filtry. Wyczyść filtr typu lub frazę wyszukiwania.",
    pageLayoutHeading: "Układ strony",
    dragWindowFromListHint: "Przeciągnij okno z listy powyżej do wybranego obszaru. Zmiany widoczne na stronie po kliknięciu",
    commitChangesEmphasis: "Zatwierdź zmiany",
    saveScopeHeading: "Zakres zapisu",
    saveScopeThisUrl: "Tylko ten adres URL",
    saveScopeThisUrlHint: "Najwyższy priorytet. Zmiany dotyczą wyłącznie tego adresu URL — strona katalogu i pozostałe podstrony niezmienione.",
    saveScopeAllArticles: "Wszystkie artykuły w katalogu",
    saveScopeAllArticlesHint: "Średni priorytet. Zmiany obejmą wszystkie podstrony katalogu — strona katalogu (główna) zostaje bez zmian. Indywidualne nadpisania per-adres zostaną wyczyszczone.",
    saveScopeCatalog: "Cały katalog",
    saveScopeCatalogHint: "Najniższy priorytet. Zmiany obejmą stronę katalogu ORAZ wszystkie jego podstrony. Indywidualne nadpisania (per-adres i per-artykuł) zostaną wyczyszczone.",
    saveScopeNarrowerDisabledOnCatalogIndex:
      "Ta opcja jest dostępna tylko na podstronie katalogu (URL inny niż strona główna katalogu). Na stronie katalogu możesz zapisać tylko zakres „Cały katalog”.",
    mapEmptyPickLayout: "Wybierz layout aby zobaczyć obszary.",
    mapNoBodyAreas: "Wybrany layout nie ma obszarów body.",
    mapSectionHeader: "Nagłówek",
    mapSectionHero: "Hero (nad treścią)",
    mapSectionBody: (layoutLabel: string) => `Treść (układ: ${layoutLabel})`,
    mapSectionBelowCatalog: "Pod treścią katalogu",
    mapSectionFooter: "Stopka",
    mapAreaHeaderCol1: "Nagłówek 1",
    mapAreaHeaderCol2: "Nagłówek 2",
    mapAreaHeaderCol3: "Nagłówek 3",
    mapAreaHeaderCol4: "Nagłówek 4",
    mapAreaHeaderBottom: "Nagłówek — dolny pas",
    mapAreaFooterLeft: "Stopka lewa",
    mapAreaFooterMiddle: "Stopka środek",
    mapAreaFooterRight: "Stopka prawa",
    mapCommitMessage: "Masz niezatwierdzone zmiany. Strona nie zostanie zmieniona aż klikniesz",
    mapCancelButton: "Cofnij",
    mapCommitButton: "Zatwierdź zmiany",
    mapAreaBlockedHint: "Treść renderowana z katalogu",
    mapAreaEmptyIncompatible: "Niekompatybilny dla tego okna",
    mapAreaEmptyDropHere: "Upuść okno tutaj",
    resetToDefaultsLabel: "Przywróć ustawienia domyślne",
    resetToDefaultsTooltip:
      "Usuwa wszystkie ustawienia układu, okien i obszarów dla CAŁEGO katalogu (strony katalogu oraz wszystkich jego artykułów) — katalog wróci do zapisanych ustawień domyślnych szablonu.",
    resetToDefaultsDisabledNoCustom:
      "Ten katalog już używa ustawień domyślnych szablonu — nie ma niczego do przywrócenia.",
    saveDefaultDisabledNoChanges:
      "Ta podstrona już używa ustawień domyślnych szablonu — wprowadź zmiany w układzie, oknach lub obszarach żeby aktywować zapis.",
    editLockedInPreviewTooltip:
      "W trybie podglądu układu domyślnego edycja niedostępna. Wyłącz podgląd aby kontynuować.",
    editLockedNoPermissionTooltip:
      "Nie masz uprawnień do edycji układu strony. Skontaktuj się z administratorem aby uzyskać dostęp.",
    deleteWindowNoPermissionTooltip:
      "Nie masz uprawnień do usuwania okien. Skontaktuj się z administratorem aby uzyskać dostęp.",
    previewModeBannerTitle: "Tryb podglądu układu domyślnego",
    previewModeBannerHint:
      "wyświetlasz stronę z konfiguracją szablonu domyślnego, edycja jest zablokowana.",
    previewModeShowLabel: "Pokaż układ domyślny",
    previewModeShowTooltip:
      "Przełącza widok strony na układ domyślny (read-only). Edycja będzie niedostępna do wyłączenia podglądu.",
    previewModeExitLabel: "Wyłącz podgląd",
    previewModeExitTooltip: "Wraca do faktycznej konfiguracji strony.",
    resetToDefaultsDialogTitle: "Przywrócić ustawienia domyślne?",
    resetToDefaultsDialogMessage:
      "Operacja obejmie CAŁY katalog — strona katalogu oraz WSZYSTKIE jego podstrony / artykuły. Wszystkie indywidualne ustawienia układu, okien i obszarów (w tym nadpisania per-artykuł) zostaną usunięte. Cały katalog wróci do zapisanych ustawień domyślnych szablonu. Tej operacji nie da się cofnąć. Strona zostanie odświeżona po zakończeniu.",
    resetToDefaultsConfirmLabel: "Przywróć domyślne dla całego katalogu",
    resetToDefaultsSuccessToast: (totalDeleted: number) =>
      `Ustawienia całego katalogu przywrócone do domyślnych (usunięto ${totalDeleted} ${pluralEntryPl(totalDeleted)}). Odświeżam stronę…`,
    resetToDefaultsErrorToast: (reason: string) =>
      `Nie udało się przywrócić ustawień domyślnych: ${reason}. Odśwież stronę żeby zobaczyć aktualny stan.`,
    mapAreaDisabledHint: "Obszar wyłączony — nie renderuje się na stronie",
    mapAreaEnableAria: (label: string) => `Włącz obszar "${label}"`,
    mapAreaDisableAria: (label: string) => `Wyłącz obszar "${label}"`,
    detachFromAreaLabel: "Zdejmij z obszaru",
    detachWindowAria: (name: string) => `Zdjmij "${name}" z obszaru`,
    mapWindowMoveTooltip: (name: string, componentLabel: string) =>
      `${name} (${componentLabel}) — kliknij dla szczegółów, przeciągnij aby przenieść`,
  },

  alignmentChecker: {
    issue: {
      windowNoAllowedAreas: (name: string) =>
        `Okno "${name}" nie ma żadnych skonfigurowanych dozwolonych obszarów — nie da się go opublikować na żadnej stronie.`,
      windowNoMatchInLayout: (name: string, layoutLabel: string) =>
        `Okno "${name}" ma skonfigurowane dozwolone obszary, ale żaden z nich nie istnieje w aktualnym układzie "${layoutLabel}".`,
      containerNotRegistered: (sym: string) =>
        `Kontener "${sym}" przypisany do okien w bazie, ale Imperial CMS nie ma jego implementacji w registry.`,
      noRendererInReactCms: (sym: string) =>
        `Komponent "${sym}" ma działającą metodę getApiContent w backend, ale Imperial CMS nie ma renderera.`,
      noLayoutsForTemplate: (activeTemplate: string, pageTypeLabel: string) =>
        `Aktywny szablon "${activeTemplate}" nie ma przypisanych layoutów dla ${pageTypeLabel}. Strona nie może być wyrenderowana — system nie używa layoutów innego szablonu (kolidowałyby ze stylem szablonu aktywnego).`,
      windowsWithoutContainerMapping: (count: number, templateLabel: string) => {
        const windowsWord = pluralWindowNominativePl(count);
        const hasWord = count === 1 ? "nie ma" : "nie mają";
        const configWord = count === 1 ? "skonfigurowanego kontenera" : "skonfigurowanych kontenerów";
        return `${count} ${windowsWord} na tej podstronie ${hasWord} ${configWord}${templateLabel}. Visitor widzi je z fallback'em (kontener "clear" — bez wrappera), ale administrator powinien dodać mapping w panelu Imperial.`;
      },
      catalogWithoutContainerMapping: (
        catalogName: string,
        componentSymbol: string,
        templateLabel: string,
      ) =>
        `Katalog "${catalogName}" (${componentSymbol}) nie ma skonfigurowanego kontenera${templateLabel}. Visitor widzi treść katalogu z fallback'em (kontener "catalog_empty" — bez wrappera), ale administrator powinien dodać mapping w panelu Imperial.`,
    },
    pageType: {
      homepage: "strony głównej",
      subpage: "podstron",
    },
    issueLabels: {
      sso_missing: "SSO NIESKONFIGUROWANE",
      sso_bad_format: "SSO ZŁY FORMAT",
      sso_session_unavailable: "SSO SESJA NIEDOSTĘPNA",
      window_no_allowed_areas: "OKNO BEZ ALLOWED_AREAS",
      window_no_match_in_layout: "OKNO BEZ MATCHU W UKŁADZIE",
      no_layouts_for_domain: "BRAK LAYOUTÓW DLA DOMENY",
      no_layouts_for_template: "BRAK LAYOUTÓW DLA SZABLONU",
      container_not_registered: "KONTENER NIE ZAREJESTROWANY",
      no_renderer_in_react_cms: "BRAK RENDERERA IMPERIAL CMS",
      cms_frontend_url_not_set: "ADRES FRONTU CMS NIESKONFIGUROWANY",
      windows_without_container_mapping: "BRAK MAPPINGU KONTENERA",
      catalog_without_container_mapping: "BRAK MAPPINGU KONTENERA KATALOGU",
    },
    bannerAriaLabel: "Banner developerski — Alignment Checker",
    closeButton: {
      title: "Ukryj banner (przywrócisz go ikoną w pasku admina)",
      ariaLabel: "Ukryj banner developera",
    },
    devBadge:
      "BANNER DEVELOPERSKI · widoczny tylko w trybie deweloperskim (NODE_ENV=development)",
    fullDocs: "Pełna dokumentacja",
    openInImperialAdmin: "Otwórz w Imperial Administrator",
    sectionDomain: (domain: string) => `Bieżąca domena (${domain})`,
    linkDomainComponents: "Komponenty w domenie",
    linkComponentContainers: "Kontenery komponentów",
    linkDomainTemplates: "Szablony w domenie",
    linkTemplateLayouts: (template: string | null) =>
      template ? `Layouty szablonu ${template}` : "Layouty aktywnego szablonu",
    linkDomainLanguages: "Języki w domenie",
    sectionCmsRegistry: "CMS — rejestry globalne",
    linkTemplates: "Szablony",
    linkLayouts: "Layouty",
    linkComponents: "Komponenty",
    linkContainers: "Kontenery",
    sectionRestApi: "REST API",
    linkApiKeys: "Klucze API + Swagger",
    sectionDocs: "Dokumentacja",
    linkReactCmsDocs: "Imperial CMS docs",
    actionOpenAddContainerMapping: "Otwórz w Imperial Administrator — dodaj mapowanie kontenera",
    readMoreLinkPrefix: "Czytaj więcej",
    contextNone: "(brak)",
    contextLabel: {
      domain: "Domena:",
      template: "Szablon:",
      pageType: "Page type:",
      layout: "Layout:",
      locale: "Locale:",
      layoutsForTemplate: "Layouty szablonu:",
    },
    headerOk: "Weryfikacja systemu — Poprawnie",
    headerIssues: (count: number) =>
      `Weryfikacja systemu — ${count} ${pluralIssuePl(count)}`,
    subtitleEnv: (nodeEnv: string) =>
      `Layouty, obszary, SSO, konfiguracja Imperial ↔ Imperial CMS · NODE_ENV=${nodeEnv}`,
    okMessage:
      "System jest poprawnie skonfigurowany — układy, obszary, SSO i połączenia Imperial ↔ Imperial CMS się zgadzają.",
    docsAnchor: {
      configCors: "Konfiguracja (CORS, klucze API)",
      issueTypes: "AlignmentChecker — opis typów błędów",
      addLayoutArea: "Jak dodać layout / area",
      layoutsDbBinding: "Powiązanie layoutów z DB",
      areasDocs: "Areas (body + chrome + catalog content)",
      restApiIntegration: "REST API integration",
      templatesDbBinding: "Templaty — powiązanie z DB",
      ssoSetup: "SSO setup (cookie domain)",
    },
    generatePackage: {
      buttonLabel: "Utwórz paczkę",
      buttonTitle:
        "Wygeneruj ZIP z source code Imperial CMS do wgrania na docelową domenę (target user: unzip + npm install + npm run build + npm start)",
      inProgress: "Generowanie paczki instalacyjnej…",
      success: (filename: string) => `Paczka gotowa: ${filename}`,
      errorBuildMissing:
        "Brak wymaganego katalogu w projekcie. Zweryfikuj strukturę react-cms.",
      errorForbidden:
        "Endpoint dostępny tylko w trybie deweloperskim (NODE_ENV=development).",
      errorGeneric: (reason: string) => `Nie udało się utworzyć paczki: ${reason}`,
    },
  },

  sessionWatcher: {
    toastExpired:
      "Sesja Imperial wygasła — wylogowano. Odśwież lub zaloguj się ponownie w Imperial admin.",
    toastLoggedIn: (userId: number | null) =>
      userId ? `Zalogowano w Imperial (user #${userId}).` : "Zalogowano w Imperial.",
    toastUserChanged: (userId: number | null) =>
      userId ? `Zmiana użytkownika Imperial na #${userId}.` : "Zmiana użytkownika Imperial.",
  },

  contextToasts: {
    layoutSavedSuccess: "Układ strony zapisany",
    layoutSaveError: (reason: string) =>
      `Nie udało się zapisać układu strony: ${reason}. Odśwież stronę żeby zobaczyć aktualny stan.`,
    areasSaveError: (failures: string) =>
      `Nie udało się zapisać zmian w obszarach: ${failures}. Odśwież stronę żeby zobaczyć aktualny stan z backendu.`,
    areaStateSaveError: (reason: string) =>
      `Nie udało się zmienić widoczności obszaru: ${reason}. Zmiana cofnięta — odśwież stronę by zobaczyć aktualny stan.`,
    areaEnabledSuccess: (areaName: string) => `Obszar "${areaName}" włączony`,
    areaDisabledSuccess: (areaName: string) => `Obszar "${areaName}" wyłączony`,
    layoutSaveErrorBeforeWindows: (reason: string) =>
      `Nie udało się zapisać układu strony: ${reason}. Zmiany okien nie zostaną pokazane do czasu zapisania układu.`,
    areaSavedSingle: (areaName: string) => `Zapisano obszar "${areaName}"`,
    areasSavedPlural: (count: number) =>
      `Zapisano ${count} ${pluralAreaPl(count)}`,
    unknownError: "unknown error",
  },
};

/* ─── Plural helpers (PL) — używane wewnątrz słownika ─── */

/** PL: 1 -> "żądanie", 2-4 -> "żądania", 5+ -> "żądań" */
function pluralRequestsPl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "żądanie";
  if (rule === "few") return "żądania";
  return "żądań";
}

/** PL: 1 -> "wpis", 2-4 -> "wpisy", 5+ -> "wpisów" */
function pluralEntryPl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "wpis";
  if (rule === "few") return "wpisy";
  return "wpisów";
}

/** PL: 1 -> "obszar", 2-4 -> "obszary", 5+ -> "obszarów" */
function pluralAreaPl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "obszar";
  if (rule === "few") return "obszary";
  return "obszarów";
}

/** PL: 1 -> "problem", 2-4 -> "problemy", 5+ -> "problemów" */
function pluralIssuePl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "problem";
  if (rule === "few") return "problemy";
  return "problemów";
}

/** PL: mianownik "okno"/"okna" — "1 okno", "3 okna", "5 okien" */
function pluralWindowNominativePl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "okno";
  return "okien";
}

/** PL: dopełniacz "okna"/"okien" — "usunięcie 1 okna", "5 okien" */
function pluralWindowGenitivePl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "okna";
  if (rule === "few") return "okien";
  return "okien";
}

/** PL: "okno wróciło" / "okna wróciły" / "okien wróciło" */
function pluralWindowsReturnedPl(n: number): string {
  const rule = plPlural.select(n);
  if (rule === "one") return "okno wróciło";
  if (rule === "few") return "okna wróciły";
  return "okien wróciło";
}

/* ─── Typy ─── */

export type AdminMessagesShape = typeof adminMessagesPl;
